import React, { useCallback, useRef, useEffect } from 'react';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin';
import LexicalErrorBoundary from '@lexical/react/LexicalErrorBoundary';
import { CollaborationPlugin } from '@lexical/react/LexicalCollaborationPlugin';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { ListItemNode, ListNode } from '@lexical/list';
import { CodeHighlightNode, CodeNode } from '@lexical/code';
import { AutoLinkNode, LinkNode } from '@lexical/link';
import { TableCellNode, TableNode, TableRowNode } from '@lexical/table';
import { HorizontalRuleNode } from '@lexical/react/LexicalHorizontalRuleNode';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { TablePlugin } from '@lexical/react/LexicalTablePlugin';
import { MarkdownShortcutPlugin } from '@lexical/react/LexicalMarkdownShortcutPlugin';
import { TRANSFORMERS } from '@lexical/markdown';
import { HashtagNode } from '@lexical/hashtag';
import { HashtagPlugin } from '@lexical/react/LexicalHashtagPlugin';
import { MarkNode } from '@lexical/mark';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';

import ExampleTheme from '../LexicalEditor/themes/ExampleTheme';
import { ImageNode } from '../LexicalEditor/nodes/ImageNode';
import { MentionNode } from '../LexicalEditor/nodes/MentionNode';
import { CalloutNode } from '../LexicalEditor/nodes/CalloutNode';
import { ChecklistItemNode } from '../LexicalEditor/nodes/ChecklistItemNode';
import { EmbedNode } from '../LexicalEditor/nodes/EmbedNode';
import { ToggleNode } from '../LexicalEditor/nodes/ToggleNode';
import { SubpageNode } from '../LexicalEditor/nodes/SubpageNode';
import { ColumnContainerNode } from '../LexicalEditor/nodes/ColumnContainerNode';
import { ColumnNode } from '../LexicalEditor/nodes/ColumnNode';
import { TableOfContentsNode } from '../LexicalEditor/nodes/TableOfContentsNode';
import ImagesPlugin from '../LexicalEditor/plugins/ImagesPlugin';
import ChecklistPlugin from '../LexicalEditor/plugins/ChecklistPlugin';
import CodeHighlightPlugin from '../LexicalEditor/plugins/CodeHighlightPlugin';
import AutoLinkPlugin from '../LexicalEditor/plugins/AutoLinkPlugin';
import ListMaxIndentLevelPlugin from '../LexicalEditor/plugins/ListMaxIndentLevelPlugin';
import PasteImagePlugin from '../LexicalEditor/plugins/PasteImagePlugin';
import EnhancedLinkPlugin from '../LexicalEditor/plugins/EnhancedLinkPlugin';
import LinkUnfurlingPlugin from '../LexicalEditor/plugins/LinkUnfurlingPlugin';
import SlashCommandPlugin from '../LexicalEditor/plugins/SlashCommandPlugin';
import ColumnLayoutPlugin from '../LexicalEditor/plugins/ColumnLayoutPlugin';
import TogglePlugin from '../LexicalEditor/plugins/TogglePlugin';
import PageFloatingToolbarPlugin from './plugins/PageFloatingToolbarPlugin';
import KeyboardShortcutsPanel from './plugins/KeyboardShortcutsPanel';
import DraggableBlockPlugin from './plugins/DraggableBlockPlugin.jsx';
import ClearFormattingOnEnterPlugin from './plugins/ClearFormattingOnEnterPlugin';
import EmptyBlockPlaceholderPlugin from './plugins/EmptyBlockPlaceholderPlugin';
import TableEnhancementsPlugin from './plugins/TableEnhancementsPlugin';
import NodeSelectionToolbarPlugin from './plugins/NodeSelectionToolbarPlugin';
import CommentsPlugin from './plugins/CommentsPlugin';

import './scss/PageEditor.scss';

// Check if a Lexical JSON state is the default empty state (root with a single empty paragraph)
function isEmptyEditorState(json) {
  if (!json?.root?.children) return true;
  const children = json.root.children;
  if (children.length === 0) return true;
  if (children.length === 1) {
    const child = children[0];
    if (child.type === 'paragraph' && (!child.children || child.children.length === 0)) {
      return true;
    }
  }
  return false;
}

// Plugin to load initial content (used in non-collaborative mode only)
function InitialContentPlugin({ initialContent }) {
  const [editor] = useLexicalComposerContext();
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    // Load initial content when both editor and content are available
    if (editor && initialContent && typeof initialContent === 'object') {
      try {
        // If we've already loaded once, don't reload to prevent loops
        if (hasLoadedRef.current) {
          return;
        }

        // Skip if editor already has content (e.g. from a previous collab session)
        const currentState = editor.getEditorState().toJSON();
        if (!isEmptyEditorState(currentState)) {
          hasLoadedRef.current = true;
          return;
        }

        const contentStr = JSON.stringify(initialContent);
        const editorState = editor.parseEditorState(contentStr);
        editor.setEditorState(editorState);
        hasLoadedRef.current = true;
      } catch (error) {
        console.error('Error loading initial content:', error);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, initialContent]); // Now depend on initialContent too

  return null;
}

// Plugin to catch known @lexical/yjs errors (e.g. #94 during paste) that propagate
// uncaught from Yjs transactions. The paste content IS inserted locally; only the
// Yjs sync fails. After catching, we mark the editor dirty so the next periodic
// save will persist the content to the REST API.
function YjsSyncErrorHandler() {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    const handler = (event) => {
      if (event.message?.includes('Minified Lexical error #94')) {
        event.preventDefault();
        console.warn('[PageEditor] Suppressed Lexical/Yjs sync error #94 (paste). Content is safe locally.');
        // Nudge the editor so the next periodic save picks up the pasted content
        setTimeout(() => {
          editor.update(() => {}, { tag: 'collaboration' });
        }, 500);
      }
    };
    window.addEventListener('error', handler);
    return () => window.removeEventListener('error', handler);
  }, [editor]);

  return null;
}

// Plugin to periodically persist editor content to REST API during collaborative editing.
// This keeps Page.content in sync so reloads/viewers see recent content.
function CollaborativePeriodicSavePlugin({ onSave, intervalMs = 30000 }) {
  const [editor] = useLexicalComposerContext();
  const lastSavedRef = useRef(null);
  const dirtyRef = useRef(false);
  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;

  const doSave = useCallback(() => {
    if (!dirtyRef.current) return;
    dirtyRef.current = false;

    editor.getEditorState().read(() => {
      const json = editor.getEditorState().toJSON();
      const jsonStr = JSON.stringify(json);
      // Skip saving content that is just the default empty editor state
      if (isEmptyEditorState(json)) return;
      if (jsonStr !== lastSavedRef.current) {
        lastSavedRef.current = jsonStr;
        onSaveRef.current(json);
      }
    });
  }, [editor]);

  useEffect(() => {
    // Mark dirty on any editor update
    const unregister = editor.registerUpdateListener(({ dirtyElements, dirtyLeaves }) => {
      if (dirtyElements.size > 0 || dirtyLeaves.size > 0) {
        dirtyRef.current = true;
      }
    });
    return unregister;
  }, [editor]);

  useEffect(() => {
    const interval = setInterval(doSave, intervalMs);

    // Flush unsaved changes on unmount (e.g. navigating away)
    return () => {
      clearInterval(interval);
      doSave();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, intervalMs, doSave]);

  return null;
}

// Debounce helper – cleans up pending timer on unmount to prevent stale saves
function useDebounce(callback, delay) {
  const timeoutRef = useRef(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, []);

  return useCallback((...args) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      callback(...args);
    }, delay);
  }, [callback, delay]);
}

const PageEditor = ({
  pageId,
  initialContent,
  onContentChange,
  readOnly = false,
  collaborative = false,
  providerFactory,
  currentUser,
}) => {
  const debouncedOnChange = useDebounce(onContentChange, 1000);
  const cursorsContainerRef = useRef(null);

  const handleChange = (editorState, editor) => {
    // Don't trigger onChange if in read-only or collaborative mode
    if (readOnly || collaborative) return;

    editorState.read(() => {
      const json = editorState.toJSON();
      // Never save the default empty editor state over existing content
      if (isEmptyEditorState(json)) return;
      debouncedOnChange(json);
    });
  };

  // Initial editor state for CollaborationPlugin bootstrap.
  // When shouldBootstrap is true and the Yjs doc is empty after sync,
  // this function loads the existing page content into the editor.
  const initialEditorState = initialContent
    ? (editor) => {
        try {
          const contentStr = JSON.stringify(initialContent);
          const parsed = editor.parseEditorState(contentStr);
          editor.setEditorState(parsed, { tag: 'history-merge' });
        } catch (error) {
          console.error('Error bootstrapping collaborative editor:', error);
        }
      }
    : null;

  const editorConfig = {
    namespace: 'PageEditor',
    theme: ExampleTheme,
    editable: !readOnly, // Make editor non-editable in read-only mode
    onError(error) {
      console.error('Lexical error:', error);
    },
    nodes: [
      HeadingNode,
      QuoteNode,
      ListNode,
      ListItemNode,
      CodeNode,
      CodeHighlightNode,
      AutoLinkNode,
      LinkNode,
      TableNode,
      TableCellNode,
      TableRowNode,
      HorizontalRuleNode,
      CalloutNode,
      ChecklistItemNode,
      ImageNode,
      MentionNode,
      HashtagNode,
      EmbedNode,
      ToggleNode,
      SubpageNode,
      ColumnContainerNode,
      ColumnNode,
      TableOfContentsNode,
      MarkNode,
    ],
  };

  return (
    <div className={`page-editor ${readOnly ? 'read-only' : ''}`}>
      <LexicalComposer initialConfig={editorConfig}>
        <div className="page-editor-container">
          <div className="page-editor-inner" ref={cursorsContainerRef}>
            <RichTextPlugin
              contentEditable={
                <ContentEditable
                  className={`page-editor-content-editable ${readOnly ? 'read-only' : ''}`}
                />
              }
              placeholder={null}
              ErrorBoundary={LexicalErrorBoundary}
            />

            {/* Collaborative mode: CollaborationPlugin handles sync, cursors, and undo/redo */}
            {collaborative && providerFactory ? (
              <>
                <CollaborationPlugin
                  id={`page:${pageId}`}
                  providerFactory={providerFactory}
                  shouldBootstrap={true}
                  username={currentUser?.name || 'Anonymous'}
                  cursorColor={currentUser?.color || '#E57373'}
                  cursorsContainerRef={cursorsContainerRef}
                  initialEditorState={initialEditorState}
                  awarenessData={{
                    id: currentUser?.id,
                    picture: currentUser?.picture,
                  }}
                />
                <CollaborativePeriodicSavePlugin onSave={onContentChange} />
                <YjsSyncErrorHandler />
              </>
            ) : (
              <>
                {/* Non-collaborative mode: standard plugins */}
                <HistoryPlugin />
                <OnChangePlugin onChange={handleChange} />
                <InitialContentPlugin initialContent={initialContent} />
              </>
            )}

            {!readOnly && !collaborative && <AutoFocusPlugin />}
            <CodeHighlightPlugin />
            <ListPlugin />
            <LinkPlugin />
            <TablePlugin />
            {!readOnly && <TableEnhancementsPlugin />}
            <AutoLinkPlugin />
            <HashtagPlugin />
            <ListMaxIndentLevelPlugin maxDepth={7} />
            {!readOnly && <MarkdownShortcutPlugin transformers={TRANSFORMERS} />}
            {!readOnly && <ImagesPlugin uploadContext="page" />}
            {!readOnly && <PasteImagePlugin uploadContext="page" pageId={pageId} />}
            <EnhancedLinkPlugin />
            <LinkUnfurlingPlugin />
            {!readOnly && <SlashCommandPlugin />}
            {!readOnly && <ColumnLayoutPlugin />}
            <ChecklistPlugin />
            <TogglePlugin />
            {!readOnly && <DraggableBlockPlugin />}
            {!readOnly && <ClearFormattingOnEnterPlugin />}
            {!readOnly && <EmptyBlockPlaceholderPlugin />}
            {!readOnly && <PageFloatingToolbarPlugin />}
            {!readOnly && <NodeSelectionToolbarPlugin />}
            {!readOnly && <KeyboardShortcutsPanel />}
            <CommentsPlugin pageId={pageId} readOnly={readOnly} />
          </div>
        </div>
      </LexicalComposer>
    </div>
  );
};

export default PageEditor;
